// ═════════════════════════════════════════════════════════════════
// COTISATIONS DmfA SELON LA CATÉGORIE D'EMPLOYEUR — logique PURE sur l'instantané ONSS
//
// Source primaire : ONSS, fichier des taux DmfA 2026/3 (dmfa_rate_2026_3.zip : NOSSWorkerAndContributionComb +
// NOSSContributionRate), lue le 19/10/2026, moissonnée par `scripts/veille/moisson-onss-cotisations.mjs`
// dans `veille/onss-cotisations-dmfa.json`.
//
// POURQUOI. Le portail DmfA Web coche les cotisations d'après la catégorie de l'employeur ; notre calcul émettait une
// liste fixe (255 · 256 · 809 · 810 · 831 · 859). Mesuré le 19/10/2026 sur IsolAction (catégorie 035, CP 336) :
// portail 4,11 € de net dû, nous 6,49 € — il nous manquait le FFE « sans finalité commerciale » (811 au lieu de 809)
// et le fonds sectoriel (830), et les fonds sectoriels étaient absents de TOUTES les CP hors CP 200.
//
// Ce module ne rend que les deux familles qui dépendent de la catégorie : le FFE de base et les fonds sectoriels dus
// d'office. 255 · 256 · 810 · 859 restent ceux de LB.onss.cotisationsDmfa (mêmes taux pour toutes les catégories,
// vérifiés contre le fichier ONSS).
// Verrou : __tests__/cotisations-dmfa-categorie.test.js
// ═════════════════════════════════════════════════════════════════
import SNAP from '@/veille/onss-cotisations-dmfa.json';
import { categoriesPourCp } from './onss-fse-officiel';

/** Les codes de cotisation que ce module décide (à retirer de la liste fixe quand la catégorie est connue). */
export const CODES_PAR_CATEGORIE = ['253', '809', '811', '820', '825', '830', '831', '832', '833', '835'];

/** « 39 » devient « 039 » ; `null` si ce n'est pas une catégorie. */
export function categorieNormalisee(v) {
  const s = String(v ?? '').trim();
  return /^\d{1,3}$/.test(s) ? s.padStart(3, '0') : null;
}

/** La catégorie est-elle dans l'instantané de l'ONSS ? */
export function categorieConnue(categorie) {
  const c = categorieNormalisee(categorie);
  return !!(c && SNAP.categories?.[c]);
}

// pourcentage → fraction, arrondi à 6 décimales (0,34 / 100 donnerait 0,0034000000000000002)
const pct = (x) => Math.round((x / 100) * 1e6) / 1e6;
const tauxDe = (t, grand) => pct(grand && t.t5 != null ? t.t5 : t.t0);

/**
 * @param {{categorie:string|number, ouvrier:boolean, effectif?:number, finaliteCommerciale?:boolean}} p
 * @returns {null | {categorie:string, ffe:{code:string,taux:number}|null, fse:Array<{code:string,taux:number,minimum:number|null,forfait:number|null}>, vacances:{code:string,taux:number}|null}}
 *          `null` si la catégorie est inconnue : l'appelant garde alors sa liste d'avant.
 */
export function cotisationsParCategorie({ categorie, ouvrier, effectif = 0, finaliteCommerciale = true }) {
  const cat = categorieNormalisee(categorie);
  const fiche = cat && SNAP.categories?.[cat];
  if (!fiche) return null;
  const profil = ouvrier ? fiche.ouvrier : fiche.employe;
  const grand = Number(effectif) >= 20; // 20 travailleurs et plus : type 5 (ONSS)

  let ffe = null;
  const f809 = profil.ffe?.['809'];
  const f811 = profil.ffe?.['811'];
  if (f809 && f811) ffe = finaliteCommerciale ? { code: '809', taux: tauxDe(f809, grand) } : { code: '811', taux: pct(f811.t0) };
  else if (f811) ffe = { code: '811', taux: pct(f811.t0) };
  else if (f809) ffe = { code: '809', taux: tauxDe(f809, grand) };

  // `minimum` = minimum trimestriel à temps plein (MinimumFlatRate, ex. 825 de la catégorie 083 : 80 €) et `forfait` = montant
  // trimestriel à temps plein (IsFlatRate, ex. 826 : 25 €), tous deux × µ. Mesuré au portail le 20/10/2026 (Saidiya : 80 × 0,34 = 27,20 ; 25 × 0,34 = 8,50).
  const fse = Object.entries(profil.fse || {})
    .map(([code, t]) => ({ code, taux: tauxDe({ t0: t.t0 ?? 0, t5: t.t5 }, grand), ...(t.min != null ? { minimum: t.min } : {}), ...(t.forfait != null ? { forfait: t.forfait } : {}) }))
    .filter((l) => l.taux > 0 || l.forfait > 0);
  // 253 : cotisation trimestrielle « vacances annuelles » (ONVA) des OUVRIERS, 5,57 % de la base à 108 % ; jamais pour un employé.
  const v = ouvrier ? fiche.ouvrier.vacances : null;
  const vacances = v && v.t0 > 0 ? { code: '253', taux: pct(v.t0) } : null;
  return { categorie: cat, ffe, fse, vacances };
}

/**
 * Fonds sectoriels COMMUNS à toutes les catégories que l'ONSS rattache à une CP (annexe 27) : quand la catégorie du dossier est
 * douteuse ou absente, on ne choisit pas la catégorie, on ne garde que ce sur quoi TOUTES s'accordent (même code, même taux, même
 * minimum). Mesuré le 20/10/2026 : CP 201, les 8 catégories rattachées portent le 832 à 0,10 % (< 20 travailleurs) ; le portail l'a
 * ajouté aux P'tits Marins (3,11 € sur 3 113,97 €). Rend [] dès que les catégories divergent : jamais un tirage au sort.
 * @returns {Array<{code:string,taux:number,minimum?:number,forfait?:number}>}
 */
export function fseCommunsPourCp({ cp, ouvrier, effectif = 0 }) {
  const cats = categoriesPourCp(String(cp ?? '')).map((b) => b.categorie).filter(categorieConnue);
  if (!cats.length) return [];
  const listes = cats.map((c) => cotisationsParCategorie({ categorie: c, ouvrier, effectif })?.fse || []);
  const cle = (l) => `${l.code}|${l.taux}|${l.minimum ?? ''}|${l.forfait ?? ''}`;
  const communes = listes[0].filter((l) => listes.every((autre) => autre.some((a) => cle(a) === cle(l))));
  return communes;
}
